// client/src/components/MismatchAlert.jsx
// Alert banner shown when location mismatches have been detected.
// Sticks to the top of the page content area so it's always visible.

import React, { useState } from "react";
import { formatEasternTime, getEasternTimeZoneLabel } from "../utils/timezone";

const MismatchAlert = ({ mismatches = [], onRunAudit, auditRunning }) => {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || mismatches.length === 0) return null;

  return (
    <div style={{
      background: "linear-gradient(135deg, #fef2f2, #fff5f5)",
      border: "1.5px solid #fca5a5",
      borderLeft: "5px solid #ef4444",
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 24,
      boxShadow: "0 2px 12px rgba(239,68,68,0.12)",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: "#fee2e2", display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
        }}>
          🚨
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: "#b91c1c", fontSize: 15 }}>
            Location Mismatch Detected — {mismatches.length} Order{mismatches.length !== 1 ? "s" : ""}
          </div>
          <div style={{ color: "#dc2626", fontSize: 13, marginTop: 2 }}>
            These orders have been automatically held in ShipHero to prevent incorrect fulfillment.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{
              padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "1.5px solid #fca5a5", background: "white",
              color: "#b91c1c", cursor: "pointer",
            }}
          >
            {expanded ? "Hide" : "Details"}
          </button>
          <button
            onClick={onRunAudit}
            disabled={auditRunning}
            style={{
              padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              border: "none", background: auditRunning ? "#fca5a5" : "#ef4444",
              color: "white", cursor: auditRunning ? "not-allowed" : "pointer",
            }}
          >
            {auditRunning ? "Auditing..." : "Re-audit"}
          </button>
          <button
            onClick={() => setDismissed(true)}
            style={{
              padding: "7px 10px", borderRadius: 8, fontSize: 16, fontWeight: 600,
              border: "1.5px solid #fca5a5", background: "white",
              color: "#9ca3af", cursor: "pointer", lineHeight: 1,
            }}
            title="Dismiss alert"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Expanded mismatch table */}
      {expanded && (
        <div style={{ marginTop: 16 }}>
          <div style={{
            borderRadius: 8, overflow: "hidden",
            border: "1px solid #fecaca",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fee2e2" }}>
                  <th style={thStyle}>Order</th>
                  <th style={thStyle}>Shopify Location</th>
                  <th style={thStyle}>ShipHero Warehouse</th>
                  <th style={thStyle}>Action Taken</th>
                  <th style={thStyle}>Detected</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((m, i) => (
                  <tr key={m.orderId || i} style={{
                    background: i % 2 === 0 ? "white" : "#fff8f8",
                    borderTop: "1px solid #fecaca",
                  }}>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>
                        {m.orderNumber || m.orderId}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 6,
                        background: "#fee2e2", color: "#b91c1c",
                        fontSize: 12, fontWeight: 600,
                      }}>
                        {m.shopifyLocationName || m.shopifyLocationId || "Unknown"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        padding: "2px 8px", borderRadius: 6,
                        background: "#fef3c7", color: "#92400e",
                        fontSize: 12, fontWeight: 600,
                      }}>
                        {m.shipheroWarehouse || "Unknown"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <ActionBadge action={m.action} />
                    </td>
                    <td style={{ ...tdStyle, color: "#64748b" }}>
                      {m.auditedAt ? `${formatEasternTime(m.auditedAt)} ${getEasternTimeZoneLabel()}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ActionBadge = ({ action }) => {
  const styles = {
    SKIPPED_NO_SHOPIFY_MATCH: { bg: "#e2e8f0", color: "#334155", label: "Skipped (No Shopify Match)" },
    CANCELLED_AND_HELD: { bg: "#dcfce7", color: "#166534", label: "Cancelled + Held" },
    LINE_ITEMS_CANCELLED: { bg: "#dcfce7", color: "#166534", label: "Line Items Cancelled" },
    HOLD_APPLIED:  { bg: "#dcfce7", color: "#166534", label: "Hold Applied" },
    NEUTRALIZATION_FAILED: { bg: "#fee2e2", color: "#991b1b", label: "Action Failed" },
    HOLD_FAILED:   { bg: "#fee2e2", color: "#991b1b", label: "Hold Failed" },
    NONE:          { bg: "#f1f5f9", color: "#475569", label: "No Action" },
    ERROR:         { bg: "#fef3c7", color: "#92400e", label: "Error" },
  };
  const s = styles[action] || styles.NONE;
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 6,
      background: s.bg, color: s.color,
      fontSize: 12, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
};

const thStyle = {
  padding: "10px 12px", textAlign: "left",
  fontSize: 11, fontWeight: 700,
  color: "#991b1b", textTransform: "uppercase", letterSpacing: "0.05em",
};

const tdStyle = {
  padding: "10px 12px",
};

export default MismatchAlert;
